import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import { Link } from 'react-router-dom';
import { useRef } from 'react';

const aboutSEO = {
  title: '关于我们 - 女神漫画网 | 女神漫画下拉式在线阅读平台',
  description:
    '女神漫画网是一个专为手机端优化的漫画在线阅读平台，提供女神漫画下拉式阅读模式。我们致力于为用户提供流畅的漫画阅读体验，涵盖韩漫、日漫、国漫等热门漫画资源。',
  keywords:
    '女神漫画网,女神漫画下拉式,关于女神漫画,漫画阅读平台,下拉式漫画在线阅读',
  canonicalUrl: 'https://nvshenmh.com/about',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于女神漫画网',
    description:
      '女神漫画网是专为手机端优化的下拉式漫画在线阅读平台',
    url: 'https://nvshenmh.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '女神漫画网',
      url: 'https://nvshenmh.com/',
    },
  },
};

const breadcrumbData = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首页',
      item: 'https://nvshenmh.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: '关于我们',
      item: 'https://nvshenmh.com/about',
    },
  ],
};

const faqData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '女神漫画下拉式阅读是什么意思？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '女神漫画下拉式阅读是一种专为手机端优化的漫画阅读模式。用户只需向下滑动屏幕即可连续阅读漫画内容，无需翻页操作，提供流畅的沉浸式阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '女神漫画网是否需要付费？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '女神漫画网上的所有漫画作品均为免费阅读，用户无需支付任何费用即可享受下拉式阅读体验。我们提供女神漫画下拉式免费全集，让读者畅享漫画乐趣。',
      },
    },
    {
      '@type': 'Question',
      name: '女神漫画网支持哪些类型的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '女神漫画网涵盖丰富的漫画类型，包括女神漫画下拉式韩漫、日漫、国漫等。类型覆盖热血、恋爱、奇幻、悬疑、科幻、美食、武侠等多种题材，满足不同读者的需求。',
      },
    },
    {
      '@type': 'Question',
      name: '如何在手机端获得最佳阅读体验？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '女神漫画网专为手机端优化设计，您只需使用手机浏览器访问女神漫画网，即可享受下拉式流畅滚动阅读体验。建议使用Chrome或Safari等主流浏览器获得最佳效果。',
      },
    },
    {
      '@type': 'Question',
      name: '漫画更新频率是怎样的？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '女神漫画网上的热门漫画会定期更新，大部分人气作品保持每周更新的节奏。您可以在每部漫画的详情页查看具体的更新时间和章节信息。',
      },
    },
    {
      '@type': 'Question',
      name: '如何联系女神漫画网客服？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过以下方式联系女神漫画网客服团队：发送邮件至 contact@nvshenmh.com，或添加客服QQ：1234567890。我们会在工作时间内尽快回复您的咨询。',
      },
    },
  ],
};

export default function AboutPage() {
  const contactRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <SEO data={aboutSEO} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }}
      />

      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <nav
            className="flex items-center gap-2 text-sm text-foreground-500"
            aria-label="面包屑导航"
          >
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Brand Intro */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-[800px] mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 text-center">
              关于女神漫画网
            </h1>
            <div className="prose prose-sm max-w-none text-foreground-600">
              <p className="text-sm leading-relaxed mb-4">
                女神漫画网成立于2024年，是一个专为手机端优化的漫画在线阅读平台。我们专注于提供
                <strong>女神漫画下拉式在线阅读</strong>
                体验，让读者无需翻页，只需向下滑动即可流畅地阅读整部漫画。无论是
                <strong>女神漫画下拉式韩漫</strong>、日漫还是国漫，都能在这里找到。
              </p>
              <p className="text-sm leading-relaxed mb-4">
                我们的核心理念是"流畅阅读，沉浸体验"。与传统漫画阅读方式相比，
                <strong>女神漫画下拉式</strong>
                模式更适合手机端的阅读习惯，大幅减少了翻页带来的割裂感。我们致力于打造最好的
                <strong>下拉式漫画大全</strong>
                平台，让每一位漫画爱好者都能享受极致的阅读体验。
              </p>
              <p className="text-sm leading-relaxed mb-4">
                女神漫画网目前收录了数百部热门漫画作品，涵盖多种题材类型。我们提供
                <strong>女神漫画下拉式免费全集</strong>
                阅读，所有漫画作品均可免费畅读。平台持续更新最新章节，确保读者能够第一时间追更心仪的作品。
              </p>
            </div>
          </div>
        </section>

        {/* Features / Reputation */}
        <section className="bg-background-100/70 py-14 md:py-18">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              为什么选择女神漫画网
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-smartphone-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mb-2">
                  手机端优化
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  专为手机屏幕设计的女神漫画下拉式阅读体验，流畅滚动，随时随地畅享漫画。
                </p>
              </div>

              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-book-read-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mb-2">
                  海量资源
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  涵盖女神漫画下拉式韩漫、日漫、国漫等热门作品，满足各类漫画爱好者的需求。
                </p>
              </div>

              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-currency-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mb-2">
                  完全免费
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  女神漫画下拉式免费全集阅读，无需付费即可享受所有漫画内容，追漫零负担。
                </p>
              </div>

              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-refresh-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950 mb-2">
                  每日更新
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  热门漫画定期更新，最新章节第一时间推送，不错过每一话精彩内容。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-18">
          <div className="max-w-[800px] mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.mainEntity.map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-xl bg-background-50 border border-background-200 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                    <h3 className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.name}
                    </h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section
          className="bg-background-100/70 py-14 md:py-18"
          ref={contactRef}
        >
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="max-w-[600px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                如果您有任何问题或建议，欢迎通过以下方式与我们取得联系
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="p-5 rounded-xl bg-background-50 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-800 mb-1">
                    电子邮箱
                  </h3>
                  <p className="text-xs text-foreground-500">
                    contact@nvshenmh.com
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-50 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-qq-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-800 mb-1">
                    客服QQ
                  </h3>
                  <p className="text-xs text-foreground-500">1234567890</p>
                </div>
                <div className="p-5 rounded-xl bg-background-50 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <h3 className="text-xs font-semibold text-foreground-800 mb-1">
                    工作时间
                  </h3>
                  <p className="text-xs text-foreground-500">
                    周一至周五 9:00-18:00
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}